import { Component, ElementRef, HostListener, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, switchMap, of, catchError, tap } from 'rxjs';
import { ApiService } from '../api/api.service';
import { GlobalSearchKind, GlobalSearchResult } from '../api/models';

/** Group heading and the page each kind of hit opens. */
const KINDS: Record<GlobalSearchKind, { label: string; link: (id: number) => string }> = {
  Customer:        { label: 'Customers',         link: id => `/customers/${id}/statement` },
  Supplier:        { label: 'Suppliers',         link: id => `/vendors/${id}/statement` },
  Invoice:         { label: 'Invoices',          link: id => `/invoices/${id}` },
  CustomerReceipt: { label: 'Received payments', link: id => `/payments/receipt/${id}` },
  CreditNote:      { label: 'Credit notes',      link: id => `/credits/customer/${id}` },
  Refund:          { label: 'Refunds',           link: id => `/refunds/${id}` },
  Bill:            { label: 'Bills',             link: id => `/bills/${id}` },
  SupplierPayment: { label: 'Bill payments',     link: id => `/payments/disbursement/${id}` },
  SupplierCredit:  { label: 'Supplier credits',  link: id => `/credits/vendor/${id}` },
  Expense:         { label: 'Expenses',          link: id => `/expenses/${id}` },
  RefundReceipt:   { label: 'Refund receipts',   link: id => `/refund-receipts/${id}` },
  Journal:         { label: 'Journal entries',   link: id => `/journal/${id}` },
  Account:         { label: 'Accounts',          link: id => `/accounts/${id}/edit` },
  Product:         { label: 'Products & services', link: id => `/products/${id}/edit` }
};

interface Group { label: string; items: { hit: GlobalSearchResult; index: number }[]; }

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="gs" [class.open]="open()">
      <span class="icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      </span>
      <input #box type="search" [(ngModel)]="query" (ngModelChange)="onType($event)"
             (focus)="onFocus()" (keydown)="onKey($event)"
             placeholder="Search name, number or amount" aria-label="Search" autocomplete="off" />
      <kbd class="hint" aria-hidden="true">/</kbd>

      @if (open()) {
        <div class="panel" (mousedown)="$event.preventDefault()">
          @if (query.trim().length < 2) {
            <div class="state">Type at least 2 characters: a customer or supplier name, a document number, or an amount.</div>
          } @else if (loading() && hits().length === 0) {
            <div class="state">Searching…</div>
          } @else if (hits().length === 0) {
            <div class="state">No matches for "{{ query.trim() }}"</div>
          } @else {
            @for (g of groups(); track g.label) {
              <div class="group">{{ g.label }}</div>
              @for (it of g.items; track it.index) {
                <button type="button" class="hit" [class.active]="it.index === active()"
                        (mouseenter)="active.set(it.index)" (click)="go(it.hit)">
                  <span class="main">
                    @if (it.hit.number) { <span class="num">{{ it.hit.number }}</span> }
                    <span class="name">{{ it.hit.name }}</span>
                  </span>
                  <span class="meta">
                    @if (it.hit.date) { <span>{{ it.hit.date | date:'dd/MM/yyyy' }}</span> }
                    @if (it.hit.amount !== null) { <span class="amt">{{ it.hit.amount | number:'1.2-2' }}</span> }
                  </span>
                </button>
              }
            }
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; flex: 1; max-width: 460px; min-width: 0; }
    .gs { position: relative; display: flex; align-items: center; }
    .icon { position: absolute; left: 10px; width: 16px; height: 16px; color: var(--color-text-subtle); pointer-events: none; }
    .icon svg { width: 16px; height: 16px; display: block; }
    input {
      width: 100%; box-sizing: border-box;
      padding: 7px 34px 7px 34px;
      border: 1px solid var(--color-border); border-radius: var(--radius-full);
      background: var(--color-surface); color: var(--color-text);
      font: inherit; font-size: var(--fs-sm);
    }
    input:focus { outline: none; border-color: var(--color-brand); box-shadow: 0 0 0 3px var(--color-brand-soft); }
    input::-webkit-search-cancel-button { display: none; }
    .hint {
      position: absolute; right: 10px; padding: 0 6px; border: 1px solid var(--color-border); border-radius: var(--radius-xs);
      font-size: 11px; color: var(--color-text-subtle); background: var(--color-bg); pointer-events: none;
    }
    .gs.open .hint { display: none; }

    .panel {
      position: absolute; top: calc(100% + 6px); left: 0; right: 0; min-width: 380px;
      max-height: 70vh; overflow-y: auto; padding: 4px 0;
      background: var(--color-surface); color: var(--color-text);
      border: 1px solid var(--color-border); border-radius: var(--radius-md); box-shadow: var(--shadow-lg);
      z-index: 60;
    }
    .state { padding: 14px 16px; color: var(--color-text-muted); font-size: var(--fs-sm); }
    .group {
      padding: 8px 14px 4px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em;
      color: var(--color-text-subtle); font-weight: 600;
    }
    .hit {
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      width: 100%; padding: 7px 14px; border: 0; background: none; cursor: pointer;
      font: inherit; font-size: var(--fs-sm); color: var(--color-text); text-align: left;
    }
    .hit.active { background: var(--color-brand-softer); }
    .main { min-width: 0; display: flex; gap: 8px; align-items: baseline; }
    .num { font-weight: 600; white-space: nowrap; }
    .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--color-text-muted); }
    .num + .name { font-size: 12px; }
    .meta { display: flex; gap: 12px; flex-shrink: 0; color: var(--color-text-muted); font-size: 12px; font-variant-numeric: tabular-nums; }
    .amt { color: var(--color-text); min-width: 70px; text-align: right; }
    @media (max-width: 720px) { .hint { display: none; } .panel { min-width: 0; } }
  `]
})
export class GlobalSearchComponent {
  private api = inject(ApiService);
  private router = inject(Router);
  private host = inject(ElementRef<HTMLElement>);
  @ViewChild('box') private box?: ElementRef<HTMLInputElement>;

  query = '';
  open = signal(false);
  loading = signal(false);
  hits = signal<GlobalSearchResult[]>([]);
  active = signal(0);

  groups = computed<Group[]>(() => {
    const out: Group[] = [];
    const byKind = new Map<string, Group>();
    this.hits().forEach((hit, index) => {
      const label = KINDS[hit.kind]?.label ?? hit.kind;
      let g = byKind.get(label);
      if (!g) { g = { label, items: [] }; byKind.set(label, g); out.push(g); }
      g.items.push({ hit, index });
    });
    // Keyboard order follows the grouped display order.
    let i = 0;
    for (const g of out) for (const it of g.items) it.index = i++;
    return out;
  });
  private ordered = computed(() => this.groups().flatMap(g => g.items.map(it => it.hit)));

  private typed = new Subject<string>();
  private sub: Subscription = this.typed.pipe(
    debounceTime(250),
    distinctUntilChanged(),
    tap(() => this.loading.set(true)),
    switchMap(q => q.length < 2 ? of([]) : this.api.globalSearch(q).pipe(catchError(() => of([]))))
  ).subscribe(list => {
    this.hits.set(list);
    this.active.set(0);
    this.loading.set(false);
  });

  onType(value: string): void {
    this.open.set(true);
    this.typed.next((value ?? '').trim());
  }

  onFocus(): void { this.open.set(true); }

  onKey(e: KeyboardEvent): void {
    const n = this.ordered().length;
    if (e.key === 'ArrowDown' && n) { e.preventDefault(); this.active.set((this.active() + 1) % n); }
    else if (e.key === 'ArrowUp' && n) { e.preventDefault(); this.active.set((this.active() - 1 + n) % n); }
    else if (e.key === 'Enter' && n) { e.preventDefault(); this.go(this.ordered()[this.active()]); }
    else if (e.key === 'Escape') { this.open.set(false); this.box?.nativeElement.blur(); }
  }

  go(hit: GlobalSearchResult | undefined): void {
    const kind = hit && KINDS[hit.kind];
    if (!hit || !kind) return;
    this.open.set(false);
    this.box?.nativeElement.blur();
    this.router.navigateByUrl(kind.link(hit.id));
  }

  /** "/" anywhere (outside a text field) jumps to the search box. */
  @HostListener('document:keydown', ['$event'])
  onDocKey(e: KeyboardEvent): void {
    if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
    e.preventDefault();
    this.box?.nativeElement.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: Event): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
  }

  ngOnDestroy(): void { this.sub.unsubscribe(); }
}
